import {IFilterFacet} from 'plugins/modules/filter/types';
import React, {ChangeEventHandler, FC, useCallback, useMemo, useState} from 'react';
import {FilterCheckbox} from 'plugins/modules/filter/components/Checkbox';
import {FilterSearch} from 'plugins/modules/filter/components/FilterSearch';
import {getFilterUnit} from 'plugins/modules/filter/helpers';
import style from './style.module.scss';

interface IProps {
    filterList: IFilterFacet[];
    onApply: (filter: IFilterFacet, variantCode: string) => void;
    onDiscard: (filter: IFilterFacet, variantCode: string) => void;
    searchPlaceholder?: string;
}

export const CheckboxGroupSearchFilterBlock: FC<IProps> = ({
    filterList = [],
    onApply,
    onDiscard,
    searchPlaceholder,
}) => {
    const [searchQuery, setSearchQuery] = useState<string>('');

    const handleChange = useCallback(
        (filter: IFilterFacet) => (variantCode: string, isChecked: boolean) => {
            if (isChecked) {
                onApply(filter, variantCode);

                return;
            }

            onDiscard(filter, variantCode);
        },
        [onApply, onDiscard],
    );

    const handleInputChange: ChangeEventHandler<HTMLInputElement> = useCallback((e) => {
        setSearchQuery(e.target.value);
    }, []);

    const handleClear = useCallback(() => {
        setSearchQuery('');
    }, []);

    const getFilteredVariants = useCallback(
        (filter: IFilterFacet) => {
            if (!filter.variants) {
                return [];
            }

            return filter.variants.filter(({title}) => String(title).toLowerCase().includes(searchQuery.toLowerCase()));
        },
        [searchQuery],
    );

    const placeholder = useMemo(() => {
        if (searchPlaceholder) {
            return searchPlaceholder;
        }

        return filterList.map(({title}) => title).join(', ');
    }, [filterList, searchPlaceholder]);

    const isNothingFound = useMemo(() => {
        if (!filterList.length) {
            return true;
        }

        return filterList.every((filter) => {
            const filteredVariants = getFilteredVariants(filter);

            return 0 === filteredVariants.length;
        });
    }, [filterList, getFilteredVariants]);

    return (
        <div>
            {1 < filterList.length && (
                <div className={style.search}>
                    <FilterSearch
                        isNothingFound={isNothingFound}
                        onChange={handleInputChange}
                        onClear={handleClear}
                        placeholder={placeholder}
                    />
                </div>
            )}
            {!isNothingFound && searchQuery && (
                <div className={style.content}>
                    {filterList.map((filter) => {
                        const filteredVariants = getFilteredVariants(filter);
                        const titleUnit = getFilterUnit(filter);

                        return filteredVariants.map((variant) => {
                            const {code, count, isApplied, title} = variant;

                            return (
                                <FilterCheckbox
                                    code={String(code)}
                                    count={count}
                                    isDefaultChecked={isApplied}
                                    key={`${filter.code}-${code}`}
                                    onChange={handleChange(filter)}
                                    title={String(title) + titleUnit}
                                />
                            );
                        });
                    })}
                </div>
            )}
        </div>
    );
};
